/* eslint-disable spire/fenced-imports */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import StandardControl from "@insite/shell-public/Components/StandardControl";
import ArrowDown from "@insite/shell/Components/Icons/ArrowDown";
import { getDigitalLineCardDataService } from "@mcmc/App/Services/DigitalLineCardService";
import { CustomCategoriesDefinition, CustomEditorTemplateProps } from "@mcmc/CustomFieldDefinition";
import * as React from "react";
import styled, { css } from "styled-components";

interface State {
    selectedCategoryId: string;
    categories: any[] | null;
}

type OwnProps = CustomEditorTemplateProps<string, CustomCategoriesDefinition>;
type Props = OwnProps;

class CategoriesField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedCategoryId: this.props.fieldValue,
            categories: null,
        };
    }

    async componentDidMount() {
        const data = await getDigitalLineCardDataService({});
        this.setState({ categories: data?.categories });
    }

    onRadioGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ selectedCategoryId: event.currentTarget.value });
        this.props.updateField(this.props.fieldDefinition.name, event.currentTarget.value);
    };

    handleClearSelection = () => {
        this.setState({ selectedCategoryId: "" });
        this.props.updateField(this.props.fieldDefinition.name, "");
    };

    renderCategories(categories?: typeof this.state.categories, renderClear = false) {
        if (!categories) {
            return null;
        }
        return (
            <>
                {renderClear && (
                    <Button disabled={!this.state.selectedCategoryId} onClick={this.handleClearSelection}>
                        {translate("Clear Selection")}
                    </Button>
                )}
                <ul>
                    <RadioGroup value={this.state.selectedCategoryId} onChangeHandler={this.onRadioGroupChange}>
                        {/* {renderAll && (
                            <TreeItemStyle key="all">
                                <TreeItemTitleStyle>
                                    <Radio
                                        key="all"
                                        value="all"
                                        css={css`
                                            display: inline;
                                            white-space: nowrap;
                                            vertical-align: middle;
                                            padding-top: 4px;
                                            input[type="radio"] {
                                                margin: 1px;
                                            }
                                            input[type="radio"] + label {
                                                white-space: nowrap;
                                                cursor: pointer;
                                            }
                                        `}
                                    >
                                        {translate("All")}
                                    </Radio>
                                </TreeItemTitleStyle>
                            </TreeItemStyle>
                        )} */}
                        {categories.map(category => {
                            const hasChildren = !!category.subCategories?.length;
                            return (
                                <TreeItemStyle key={category.id}>
                                    <TreeItemTitleStyle>
                                        {hasChildren && (
                                            <ArrowContainerStyle isExpanded={true}>
                                                <ArrowDown height={6} />
                                            </ArrowContainerStyle>
                                        )}
                                        <Radio
                                            key={category.id}
                                            value={category.id}
                                            css={css`
                                                display: inline;
                                                white-space: nowrap;
                                                vertical-align: middle;
                                                padding-top: 4px;
                                                input[type="radio"] {
                                                    margin: 1px;
                                                }
                                                input[type="radio"] + label {
                                                    white-space: nowrap;
                                                    cursor: pointer;
                                                }
                                            `}
                                        >
                                            {category.name}
                                        </Radio>
                                    </TreeItemTitleStyle>
                                    {hasChildren && this.renderCategories(category.subCategories)}
                                </TreeItemStyle>
                            );
                        })}
                    </RadioGroup>
                </ul>
            </>
        );
    }

    render() {
        return (
            <StandardControl fieldDefinition={this.props.fieldDefinition as any}>
                <TreeContainerStyle>{this.renderCategories(this.state.categories, true)}</TreeContainerStyle>
            </StandardControl>
        );
    }
}

export default CategoriesField;

const TreeContainerStyle = styled.div`
    margin-top: 10px;
    padding: 2px 8px;
    background-color: #fff;
    overflow: auto;
    border: 1px solid lightgray;
    border-radius: 4px;
`;

const TreeItemStyle = styled.li`
    ul {
        padding-left: 10px;
        h3,
        ul {
            margin-left: 12px;
        }
        li {
            position: relative;
        }
        li::before {
            content: "";
            width: 2px;
            height: 100%;
            background-color: rgb(216, 216, 216);
            position: absolute;
            top: 2px;
            left: -6px;
        }
        li:last-child::before {
            height: 12px;
        }
        li::after {
            content: "";
            width: 14px;
            height: 2px;
            background-color: rgb(216, 216, 216);
            position: absolute;
            top: 13px;
            left: -6px;
        }
    }
`;

const TreeItemTitleStyle = styled.h3`
    padding: 2px 0;
    display: flex;
    span {
        cursor: pointer;
        &:hover {
            font-weight: bold;
        }
    }
`;

const ArrowContainerStyle = styled.div<{ isExpanded: boolean }>`
    padding: ${props => (props.isExpanded ? "0 4px 0 6px;" : "0 5px 0 4px")};
    margin-left: ${props => (props.isExpanded ? "-5px" : "-1px")};
    display: inline-block;
    cursor: pointer;
    &:hover svg path {
        stroke-width: 4 !important;
    }
`;
